/**
 * CallFormModal - Модальное окно для создания/редактирования звонка
 *
 * Поддерживает два режима:
 * - Запланировать: создание звонка на будущее (status = 'planned')
 * - Зафиксировать: запись о уже состоявшемся звонке (status = 'completed')
 */
import React, { useState, useEffect } from 'react';
import {
  Modal,
  TextInput,
  Textarea,
  Select,
  Button,
  Group,
  Stack,
  Alert,
  Divider,
  SegmentedControl,
  Text,
} from '@mantine/core';
import { DatePickerInput, TimeInput } from '@mantine/dates';
import { IconAlertCircle, IconPhone, IconCalendar, IconClock, IconClockPlay, IconClockCheck } from '@tabler/icons-react';
import { useSession } from '../../../context/SessionContext';
import tokens from '../../../theme/designTokens';

/**
 * Форматирование полного имени контакта
 */
const formatContactName = (contact) => {
  const parts = [];
  if (contact.last_name) parts.push(contact.last_name);
  if (contact.first_name) parts.push(contact.first_name);
  if (contact.middle_name) parts.push(contact.middle_name);
  const name = parts.join(' ') || 'Без имени';
  return contact.position ? `${name} (${contact.position})` : name;
};

/**
 * Форматирование имени пользователя
 */
const formatUserName = (user) => {
  if (user.full_name) return user.full_name;
  const parts = [];
  if (user.last_name) parts.push(user.last_name);
  if (user.first_name) parts.push(user.first_name);
  return parts.join(' ') || user.email;
};

const CallFormModal = ({
  opened,
  onClose,
  onSubmit,
  call = null, // null = создание, object = редактирование
  organizationId,
  contacts = [], // Список контактов организации
  loading = false,
}) => {
  const { user: currentUser } = useSession();
  const isEditing = !!call;

  const [formData, setFormData] = useState({
    contact_id: null,
    caller_user_id: null,
    subject: '',
    comment: '',
    result: '',  // Результат звонка (для режима "Зафиксировать")
    call_date: new Date(),
    call_time: '',
  });
  const [mode, setMode] = useState('log');  // 'plan' = запланировать, 'log' = зафиксировать
  const [error, setError] = useState(null);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  const isPlanned = mode === 'plan';

  // Загрузка списка пользователей (Sales/Managers)
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoadingUsers(true);
        const response = await fetch('/api/users/sales-managers', {
          credentials: 'include',
        });
        if (response.ok) {
          const data = await response.json();
          setUsers(data.items || []);
        }
      } catch (err) {
        console.error('Failed to load users:', err);
      } finally {
        setLoadingUsers(false);
      }
    };

    if (opened) {
      fetchUsers();
    }
  }, [opened]);

  // Форматирование времени из даты
  const formatTimeFromDate = (date) => {
    if (!date) return '';
    const d = new Date(date);
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  // Инициализация формы при открытии
  useEffect(() => {
    if (opened) {
      if (call) {
        // Редактирование - заполняем данными
        const callDate = new Date(call.call_datetime);
        setFormData({
          contact_id: call.contact_id?.toString() || null,
          caller_user_id: call.caller_user_id?.toString() || null,
          subject: call.subject || '',
          comment: call.comment || '',
          result: call.result || '',
          call_date: callDate,
          call_time: formatTimeFromDate(callDate),
        });
        // Определяем режим на основе статуса
        setMode(call.status === 'planned' ? 'plan' : 'log');
      } else {
        // Создание - текущая дата/время и текущий пользователь
        const now = new Date();
        setFormData({
          contact_id: null,
          caller_user_id: currentUser?.id?.toString() || null,
          subject: '',
          comment: '',
          result: '',
          call_date: now,
          call_time: formatTimeFromDate(now),
        });
        setMode('log');  // По умолчанию режим "Зафиксировать"
      }
      setError(null);
    }
  }, [opened, call, currentUser]);

  // Подготовка данных для Select контактов
  const contactOptions = contacts.map((contact) => ({
    value: contact.id.toString(),
    label: formatContactName(contact),
  }));

  // Подготовка данных для Select пользователей
  const userOptions = users.map((user) => ({
    value: user.id.toString(),
    label: formatUserName(user),
  }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Валидация
    if (!formData.call_date) {
      setError('Укажите дату звонка');
      return;
    }

    if (!formData.call_time) {
      setError('Укажите время звонка');
      return;
    }

    // Для режима "Зафиксировать" результат обязателен
    if (!isPlanned && !formData.result?.trim()) {
      setError('Укажите результат звонка');
      return;
    }

    try {
      // Собираем дату и время в один datetime
      const [hours, minutes] = formData.call_time.split(':').map(Number);
      const callDatetime = new Date(formData.call_date);
      callDatetime.setHours(hours || 0, minutes || 0, 0, 0);

      const data = {
        organization_id: organizationId,
        contact_id: formData.contact_id ? parseInt(formData.contact_id, 10) : null,
        caller_user_id: formData.caller_user_id ? parseInt(formData.caller_user_id, 10) : null,
        subject: formData.subject?.trim() || null,
        comment: formData.comment?.trim() || null,
        call_datetime: callDatetime.toISOString(),
        // Поля для планирования
        is_planned: isPlanned,
        result: !isPlanned ? formData.result?.trim() : null,
      };

      await onSubmit(data);
    } catch (err) {
      setError(err.message || 'Ошибка при сохранении звонка');
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={isEditing ? 'Редактировать звонок' : 'Добавить звонок'}
      size="lg"
      radius="xl"
      styles={{
        content: {
          borderRadius: '16px',
        },
      }}
    >
      <form onSubmit={handleSubmit}>
        <Stack gap="md">
          {error && (
            <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
              {error}
            </Alert>
          )}

          {/* Переключатель режима (только для создания нового звонка) */}
          {!isEditing && (
            <SegmentedControl
              value={mode}
              onChange={setMode}
              fullWidth
              radius="xl"
              data={[
                {
                  value: 'log',
                  label: (
                    <Group gap="xs" justify="center">
                      <IconClockCheck size={16} />
                      <Text size="sm">Зафиксировать</Text>
                    </Group>
                  ),
                },
                {
                  value: 'plan',
                  label: (
                    <Group gap="xs" justify="center">
                      <IconClockPlay size={16} />
                      <Text size="sm">Запланировать</Text>
                    </Group>
                  ),
                },
              ]}
              styles={{
                root: {
                  backgroundColor: tokens.colors.neutral[2],
                },
              }}
            />
          )}

          {/* Подсказка по режиму */}
          <Text size="xs" c="dimmed">
            {isPlanned
              ? 'Создание напоминания о звонке на указанную дату и время'
              : 'Запись о уже состоявшемся звонке'}
          </Text>

          <Divider />

          {/* С кем был звонок */}
          <Select
            label={isPlanned ? 'С кем планируется звонок' : 'С кем был звонок'}
            placeholder="Выберите контакт"
            required={!isPlanned}
            data={contactOptions}
            value={formData.contact_id}
            onChange={(value) => setFormData({ ...formData, contact_id: value })}
            searchable
            nothingFoundMessage="Контакты не найдены"
            leftSection={<IconPhone size={16} />}
          />

          {/* Кто звонил */}
          <Select
            label="Кто звонил"
            placeholder="Выберите сотрудника"
            data={userOptions}
            value={formData.caller_user_id}
            onChange={(value) => setFormData({ ...formData, caller_user_id: value })}
            searchable
            clearable
            nothingFoundMessage={loadingUsers ? 'Загрузка...' : 'Сотрудники не найдены'}
          />

          {/* Тема звонка */}
          <TextInput
            label="Тема звонка"
            placeholder="Краткое описание темы..."
            value={formData.subject}
            onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
          />

          <Divider label={isPlanned ? 'Когда позвонить' : 'Дата и время звонка'} labelPosition="left" />

          {/* Дата и время */}
          <Group grow>
            <DatePickerInput
              label={isPlanned ? 'Дата звонка' : 'Дата'}
              placeholder="Выберите дату"
              required
              value={formData.call_date}
              onChange={(value) => setFormData({ ...formData, call_date: value })}
              leftSection={<IconCalendar size={16} />}
              valueFormat="DD.MM.YYYY"
              locale="ru"
            />
            <TimeInput
              label="Время"
              placeholder="00:00"
              required
              value={formData.call_time}
              onChange={(e) => setFormData({ ...formData, call_time: e.target.value })}
              leftSection={<IconClock size={16} />}
            />
          </Group>

          {/* Результат звонка (только для режима "Зафиксировать") */}
          {!isPlanned && (
            <>
              <Divider label="Результат звонка" labelPosition="left" />
              <Textarea
                label="Результат"
                placeholder="Договорились о встрече, отправили КП, нужен повторный звонок..."
                required
                autosize
                minRows={3}
                value={formData.result}
                onChange={(e) => setFormData({ ...formData, result: e.target.value })}
              />
            </>
          )}

          {/* Комментарий (опциональный) */}
          <Textarea
            label={isPlanned ? 'Заметка' : 'Дополнительный комментарий'}
            placeholder={isPlanned ? 'О чём нужно поговорить, подготовка...' : 'Дополнительные заметки...'}
            autosize
            minRows={2}
            value={formData.comment}
            onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
          />

          {/* Кнопки */}
          <Group justify="flex-end" mt="md">
            <Button variant="subtle" onClick={onClose}>
              Отмена
            </Button>
            <Button
              type="submit"
              loading={loading}
              leftSection={isPlanned ? <IconClockPlay size={16} /> : <IconClockCheck size={16} />}
            >
              {isEditing ? 'Сохранить' : (isPlanned ? 'Запланировать' : 'Зафиксировать')}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
};

export default CallFormModal;
